<template>
  <div class="input_group">
    <label :for="name">{{ label }}</label>


    <div :class="{'tag_input': true, 'active': isInputFocused, 'disabled': disabled, 'input_error': errorMessage}">
      <div v-for="(tag, index) in tags" :key="'tag-' + index" class="tag_input_item">
        {{ tag }}
        <button @click="removeTag(index)" class="remove-tag-btn"><i class="fa-solid fa-xmark-large" style="width: 8px"></i></button>
      </div>

      <input
        :id="name"
        :name="name"
        :type="type"
        :placeholder="placeholder"
        v-model="inputValue"
        @focusout="handleError"
        :disabled="disabled"
        v-maska="mask"
        :maxlength="maxlength"
        @keydown.enter.prevent="addTag"
        @keydown.,="addTag"
        @keydown.delete="removeLastTag"
        @focus="isInputFocused = true"
        @blur="isInputFocused = false"
        :class="{ 'input_field': true }"
      />
    </div>

    <span v-if="errorMessage" class="error_message">{{ errorMessage }}</span>
  </div>
</template>

<script setup>
import { ref, watch, defineEmits, defineProps } from 'vue';
import { vMaska } from "maska";

const props = defineProps({
  name: String,
  label: String,
  type: { type: String, default: 'text' },
  mask: { required: false, default: null },
  placeholder: String,
  modelValue: [Array],
  disabled: Boolean,
  error: String,
  maxlength: String,
});

const tags = ref([]);
const inputValue = ref('');
const errorMessage = ref(props.error);
const isInputFocused = ref(false);
const emit = defineEmits(['update:modelValue']);

watch(() => props.modelValue, (newValue) => {
  if (Array.isArray(newValue)) {
    tags.value = [...newValue];
  }
}, { immediate: true });

watch(() => props.error, (newValue) => {
  if (newValue) {
    errorMessage.value = newValue;
  }
});

const handleError = () => {
  errorMessage.value = null;
};

const addTag = (event) => {
  if (event.key === ',' || event.key === 'Enter') {
    event.preventDefault();
    let val = inputValue.value.trim();
    if (val && !tags.value.includes(val)) {
      tags.value.push(val);
      emit('update:modelValue', tags.value);
      inputValue.value = '';
    }
  }
};

const removeTag = (index) => {
  tags.value.splice(index, 1);
  emit('update:modelValue', tags.value);
};

const removeLastTag = () => {
  if (!inputValue.value && tags.value.length) {
    tags.value.pop();
    emit('update:modelValue', tags.value);
  }
};
</script>

<style scoped>
.input_group {
  display: flex;
  flex-direction: column;
}

.input_group label {
  margin-bottom: 4px;
  font-weight: 500;
  text-align: left;
}

.tag_input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 5px;
  border: 1px solid #d7d7d7;
  border-radius: 4px;
  min-height: 43px;
}

.dark .tag_input {
  background-color: #232932;
  border-color: #394251;
}

.tag_input.active {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.tag_input.disabled {
  background-color: #e9ecef;
}

.tag_input_item {
  color: #fff;
  background-color: #583a8e;
  border-radius:4px;
  margin: 2px;
  padding: 0 4px 0px 8px;
  font-size: 14px;
  height: 24px;
  display: flex;
  align-items: center;
  line-height: 24px;
}

.tag_input_item button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding-left: 8px;
  padding-top: 2px;
}

.input_field {
  background: transparent;
  flex: 1;
  border: none;
  outline: none;
  padding: 4px;
  min-width: 100px;
  font-weight: 500;
}

.dark .input_field {
  color: #b5b9d0 !important;
}

.input_field:focus {
  box-shadow: none;
}

.input_error {
  border-color: #df2b2b;
  box-shadow: 0px 0px 0px 3px rgb(255 136 136 / 30%);
}

.error_message {
  color: #df2b2b;
  font-weight: 500;
  margin-top: 4px;
}
</style>
